<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :lockable="lockable"
            :has-lock="hasLock"
            :document-lock="documentLock"
            @force-takeover="attemptTakeover"
            @takeover="takeOver"

            :show-preview-button="shouldShowPreviewButton()"
            @click-preview="submitPreviewSite(form.language.iso, form.uri)"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :seo-data="data ? data.latest_seo : null"

            model="App\Models\TrustPage"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-300 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <base-page-fields
            class="mt-3"
            :form="form"
            :errors="errors"
            :editable-timestamps="true"
            :publishable="publishable"
            :h1-anchor-contains-shortcode="true"
        ></base-page-fields>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Author</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                two-step-create
                :creatable="false"
                :language="form.language"
                v-model="form.author"
                :should-open-new-window="true"
                model-data="Author"
                :order="{
                    'name': 'asc'
                }"
                :excluded-ids="excludedAuthors" />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Fact checked by</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                two-step-create
                :creatable="false"
                v-model="form.factChecker"
                model-data="Author"
                :should-open-new-window="true"
                :order="{
                    'name': 'asc'
                }"
                :excluded-ids="excludedFactCheckers"
                :language="form.language"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Published For</p>
            <reference
                create-text="Create new country and link country to this page"
                link-text="Link an existing country"
                two-step-create
                :creatable="false"
                :language="form.language"
                v-model="form.publishedFor"
                model-data="Country"
                :should-open-new-window="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <input-rich-text
            name="overview"
            label="Intro Text (below H1)"
            v-model="form.overview"
            class="mt-2"
            :language="form.language"
            :error="errors.overview ? errors.overview[0] : null"
            :maxlength="5000"
        />
        <p class="my-1 text-xs text-gray-600 dark:text-gray-200">You can use these available shortcodes in Intro Text (below H1) field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

        <div class="mt-2">
            <span class="text-gray-700 dark:text-gray-200">Content Blocks</span>
            <body-content
                :gallery="form.gallery"
                :language="form.language"
                v-model="form.contentBlocks"
                :errors="errors"
                :content-section-with-how-to="contentSectionWithHowTo"
                :content-section-with-bonus-list-top="contentSectionWithBonusListTop"
                :content-section-with-bonus-list-bottom="contentSectionWithBonusListBottom"
                :content-sections-with-ranking="contentSectionsWithRanking"
                :document-class="className"
                :document-id="data ? data.id : null"
                @add-content-block="addContentBlock"
                @add-content-section="addContentSection"
                @delete-content-block="deleteContentBlock"
                @delete-content-section="deleteContentSection"
                @load-content-blocks="loadContentBlocks"
                @add-how-to="howToAdded"
                @delete-how-to="howToDeleted"
                @add-bonus-list-top="bonusListTopAdded"
                @delete-bonus-list-top="bonusListTopDeleted"
                @add-bonus-list-bottom="bonusListBottomAdded"
                @delete-bonus-list-bottom="bonusListBottomDeleted"
                @add-ranking="rankingAdded"
                @delete-ranking="rankingDeleted"
            ></body-content>
        </div>

        <input-checkbox
            name="show_contact_form"
            label="Show contact form"
            class="mt-3 mb-8"
            v-model="form.show_contact_form"
            :error="errors.show_contact_form ? errors.show_contact_form[0] : null"
        />

        <contact-form
            :disabled="!form.show_contact_form"
            :form="form"
            :errors="errors"
        >
        </contact-form>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import pageFormData from "../mixins/pageFormData";
import contentBlocksForm from "../mixins/contentBlocksForm";
import BaseTable from './Table/BaseTable.vue';

export default {
  components: { BaseTable },
    mixins: [crudForm, contentBlocksForm, pageFormData],
    name: "TrustPageForm",
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                h1_anchor: this.getFormData("h1_anchor"),
                overview: this.getFormData("overview"),
                author_id: this.getFormData("author_id"),
                author: this.getFormData("author"),
                fact_checker_id: this.getFormData("fact_checker_id"),
                factChecker: this.getFormData("fact_checker"),
                published_for_id: this.getFormData("published_for_id", this.languageData.default_country_id),
                publishedFor: this.getFormData("published_for", this.languageData.default_country),
                show_contact_form: this.getFormData("show_contact_form", false),
                contact_form_heading: this.getFormData("contact_form_heading"),
                contact_form_anchor: this.getFormData("contact_form_anchor"),
                contact_form_anchor_text: this.getFormData("contact_form_anchor_text"),
                contact_form_text: this.getFormData("contact_form_text"),
                created_at: this.getFormData("created_at") || new Date(),
                created_at_override: this.getFormData("created_at_override"),
                updated_at: this.getFormData("updated_at") || new Date(),
                updated_at_override: this.getFormData("updated_at_override"),
                variants: this.getFormData('variants', []),
            },
        };
    },
    computed: {
        excludedAuthors () {
            return [this.form.fact_checker_id];
        },
        excludedFactCheckers () {
            return [this.form.author_id];
        },
    },
    watch: {
        "form.author": function (newValue) {
            this.form.author_id = newValue ? newValue.id : null;
        },
        "form.factChecker": function (newValue) {
            this.form.fact_checker_id = newValue ? newValue.id : null;
        },
        "form.publishedFor": function (newValue) {
            this.form.published_for_id = newValue ? newValue.id : null;
        },
        "form.show_contact_form": function (newValue) {
            if (!newValue) {
                this.form.contact_form_heading = null;
                this.form.contact_form_anchor = null;
                this.form.contact_form_anchor_text = null;
                this.form.contact_form_text = '';
            }
        },
    }
};
</script>
